import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { Action, ActionReducer } from '@ngrx/store';
import * as get from 'lodash/get';
import * as set from 'lodash/set';

import { ActionConstants } from './action.constants';
import { Config } from './config.interface';
import { State } from './state.interface';

export function createReducerWithConfig<T>(config: Config<T>): ActionReducer<any, Action> {
	let name = config.name;

	if (!name) {
		throw new Error('Error in createReducerWithName: name cannot be empty!');
	}

	name = name.toUpperCase();
	if (config.storeAsList) {

		const adapter: EntityAdapter<T> = createEntityAdapter<T>();

		const initialState: State<T> = adapter.getInitialState({
			// additional entity state properties
			isLoading: !config.defaultLoaded
		});

		return function create(state = initialState, action: any): ActionReducer<State<T>, Action> {
			switch (action.type) {
				case ActionConstants.FETCH + name: {
					return adapter.upsertMany([], { ...state, isLoading: true });
				}

				case ActionConstants.ADD_ONE + name: {
					return adapter.addOne(action.payload, { ...state, isLoading: false });
				}

				case ActionConstants.UPSERT_ONE + name: {
					const cleaned = adapter.removeOne(action.payload.id, { ...state, isLoading: false });
					return adapter.addOne(action.payload, cleaned);
				}

				case ActionConstants.ADD_MANY + name: {
					return adapter.addMany(action.payload, { ...state, isLoading: false });
				}

				case ActionConstants.UPSERT_MANY + name: {
					const cleaned = adapter.removeMany(action.payload.map(data => data.id), { ...state, isLoading: false });
					return adapter.addMany(action.payload, cleaned);
				}

				case ActionConstants.UPDATE_ONE + name: {
					return adapter.updateOne(action.payload, { ...state, isLoading: false });
				}

				case ActionConstants.UPDATE_MANY + name: {
					return adapter.updateMany(action.payload, { ...state, isLoading: false });
				}

				case ActionConstants.REMOVE_ONE + name: {
					return adapter.removeOne(action.payload.id, { ...state, isLoading: false });
				}

				case ActionConstants.REMOVE_MANY + name: {
					return adapter.removeMany(action.payload, { ...state, isLoading: state.isLoading });
				}

				case ActionConstants.ADD_ALL + name: {
					return adapter.setAll(action.payload, { ...state, isLoading: false });
				}

				case ActionConstants.REMOVE_ALL + name: {
					return adapter.removeAll({ ...state, selectedId: null, isLoading: false });
				}

				/*
					{
						type: 'REDUX_MODIFY_DETAILS',
						id: '328a852d-2d1d-4736-94e8-8ad5c0059d84',
						path: 'holdings[0].aladdinTicker',
						value: 'testTicker'
					}
				*/
				case ActionConstants.REDUX_MODIFY + name: {
					try {
						const datapoint = action.path;
						const id = action.id;
						if (datapoint) {
							const oldVal = get(state, `entities.${id}.${datapoint}`);
							set(state, `entities.${action.id}.${datapoint}`, action.value);
							console.log(`Modified portfolio with id: ${id}. \n Old value: ${oldVal} \n New value: ${action.value}`);
						}
					} catch (e) {
						console.log(e);
					}
					return state;
				}

				/*
					{
						type: 'REDUX_DELETE_RISK',
						id: '328a852d-2d1d-4736-94e8-8ad5c0059d84',
						path: 'id'
					}
					if path is not provided, the entire entity associated with the id is deleted
				*/
				case ActionConstants.REDUX_DELETE + name: {
					try {
						const datapoint = action.path;
						const id = action.id;
						if (datapoint) {
							set(state, `entities.${id}.${datapoint}`, undefined);
							console.log(`deleted ${datapoint} for portfolio with id: ${id}`);
						} else {
							delete state.entities[id];
							console.log(`deleted portfolio with id: ${id}`);
						}
					} catch (e) {
						console.log(e);
					}
					return state;
				}

				case ActionConstants.NO_ACTION + name: {
					return adapter.upsertMany([], { ...state, isLoading: false });
				}

				default: {
					return state;
				}
			}
		};
	} else {
		interface State {
			object: T;
		}

		const initialState: State = {
			object: null
		};

		return function create(state = initialState, action: any): State {
			switch (action.type) {
				case ActionConstants.LOAD_ONE + name: {
					return { ...state, object: action.payload };
				}

				case ActionConstants.UPSERT_ONE + name: {
					const object = state.object ? state.object : {};
					const updatedObject = Object.assign({ ...object }, action.payload);
					return { ...state, object: updatedObject };
				}

				default: {
					return state;
				}
			}
		};
	}
}
